import { supabase } from 'src/lib/supabaseClient';
import { supabaseServiceRole } from 'src/lib/supabaseServiceClient';
import { TutorAvailabilitySlot, Tutor } from './tutorsService';
import { fromZonedTime, toZonedTime, format } from 'date-fns-tz';
import { addMinutes, isAfter, isBefore, isEqual, parseISO, startOfDay, endOfDay, roundToNearestMinutes } from 'date-fns';

// Types for booking availability
export interface BookingTimeSlot {
  startTime: Date;
  endTime: Date;
  timeString: string; // Display string in target timezone
  available: boolean;
}

export interface SessionBlock {
  startTime: Date;
  endTime: Date;
  sessionId: string;
}

export interface AvailabilityWindow {
  startTime: Date;
  endTime: Date;
  tutorTimezone: string;
}

export interface UnavailablePeriod {
  startTime: Date;
  endTime: Date;
  reason: 'session' | 'elapsed' | 'buffer';
}

// Buffer time in minutes to add after each session
const BUFFER_TIME_MINUTES = 15;

// Minimum booking advance time in minutes (30 minutes)
const MIN_BOOKING_ADVANCE_MINUTES = 30;

// Time slot interval in minutes (30 minutes)
const SLOT_INTERVAL_MINUTES = 30;

class BookingAvailabilityService {
  /**
   * Get available time slots for booking a session with a tutor on a specific date
   * Improved algorithm with efficient timezone conversion
   */
  async getAvailableBookingSlots(
    tutorId: string,
    selectedDate: Date,
    targetTimezone: string,
    sessionDurationMinutes: number = 60
  ): Promise<BookingTimeSlot[]> {
    try {
      // 1. Get tutor data with availability and timezone
      const tutor = await this.getTutorWithAvailability(tutorId);
      if (!tutor || !tutor.timezone) {
        console.error('Tutor not found or timezone missing');
        return [];
      }

      // 2. Create the full day window in target timezone
      const fullDayWindow = this.createFullDayWindow(selectedDate, targetTimezone);

      // 3. Get all unavailable periods (sessions + elapsed time + buffer)
      const unavailablePeriods = await this.getUnavailablePeriods(
        tutorId,
        selectedDate,
        targetTimezone
      );

      // 4. Subtract unavailable periods to get available windows
      const availableWindows = this.subtractUnavailablePeriods(fullDayWindow, unavailablePeriods);

      // 5. Convert tutor availability from database to target timezone and merge intervals
      const tutorAvailabilityWindows = this.convertTutorAvailabilityToTargetTimezone(
        tutor.availability_slots || [],
        selectedDate,
        targetTimezone
      );

      // 6. Get intersection with available windows from step 4
      const finalWindows = this.getIntersectionWithAvailableWindows(
        availableWindows,
        tutorAvailabilityWindows
      );

      // 7. Generate all possible booking slots from final windows
      const availableSlots = this.generateBookingSlotsFromWindows(
        finalWindows,
        sessionDurationMinutes,
        targetTimezone
      );

      return availableSlots;
    } catch (error) {
      console.error('Error getting available booking slots:', error);
      return [];
    }
  }

  /**
   * Get tutor with availability data
   */
  private async getTutorWithAvailability(tutorId: string): Promise<Tutor | null> {
    try {
      // Get tutor basic info using service role to bypass RLS
      const { data: tutorData, error: tutorError } = await supabaseServiceRole
        .from('tutors')
        .select('id')
        .eq('id', tutorId)
        .single();

      if (tutorError || !tutorData) {
        throw new Error('Tutor not found');
      }

      // Get profile with timezone using service role to bypass RLS
      const { data: profileData, error: profileError } = await supabaseServiceRole
        .from('profiles')
        .select('id, timezone')
        .eq('id', tutorId)
        .single();

      if (profileError || !profileData) {
        throw new Error('Tutor profile not found');
      }

      // Get availability slots using service role to bypass RLS
      const { data: availabilityData, error: availabilityError } = await supabaseServiceRole
        .from('tutor_availability')
        .select('day_of_week, start_time, end_time, status')
        .eq('tutor_id', tutorId);

      if (availabilityError) {
        console.error('Error fetching availability:', availabilityError);
      }

      const availability_slots: TutorAvailabilitySlot[] = availabilityData?.map(slot => ({
        day_of_week: slot.day_of_week,
        start_time: slot.start_time,
        end_time: slot.end_time,
        status: slot.status as "available" | "auto_accept" | "manual_approval"
      })) || [];

      return {
        id: tutorId,
        timezone: profileData.timezone,
        availability_slots,
        // Add other required fields with defaults
        first_name: '',
        last_name: '',
        email: '',
        created_at: '',
        updated_at: ''
      };
    } catch (error) {
      console.error('Error fetching tutor data:', error);
      return null;
    }
  }

  /**
   * Create a full day window in target timezone (12:00 AM to 12:00 AM next day)
   */
  private createFullDayWindow(selectedDate: Date, targetTimezone: string): AvailabilityWindow {
    // Create immutable copies to avoid mutations
    const dateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    
    // Create start of day in target timezone
    const startOfDayInTarget = startOfDay(dateOnly);
    
    // Create start of next day in target timezone (midnight of next day)
    // This allows for 24:00:00 availability slots to be properly handled
    const nextDayDate = new Date(dateOnly.getFullYear(), dateOnly.getMonth(), dateOnly.getDate() + 1);
    const startOfNextDayInTarget = startOfDay(nextDayDate);
    
    return {
      startTime: startOfDayInTarget,
      endTime: startOfNextDayInTarget,
      tutorTimezone: targetTimezone
    };
  }

  /**
   * Get all unavailable periods (sessions + elapsed time + buffer)
   * Uses service role to bypass RLS and access sessions table
   */
  private async getUnavailablePeriods(
    tutorId: string,
    selectedDate: Date,
    targetTimezone: string
  ): Promise<UnavailablePeriod[]> {
    const unavailablePeriods: UnavailablePeriod[] = [];

    // 1. Add elapsed time period
    const now = new Date();
    const currentTimeInTarget = toZonedTime(now, targetTimezone);
    const startOfDayInTarget = startOfDay(selectedDate);

    // If the selected date is today, mark elapsed time as unavailable
    if (startOfDay(currentTimeInTarget).getTime() === startOfDayInTarget.getTime()) {
      // Round to nearest 30 minutes and add minimum booking advance time
      const roundedCurrentTime = roundToNearestMinutes(currentTimeInTarget, { nearestTo: SLOT_INTERVAL_MINUTES });
      const earliestBookingTime = addMinutes(roundedCurrentTime, MIN_BOOKING_ADVANCE_MINUTES);

      unavailablePeriods.push({
        startTime: startOfDayInTarget,
        endTime: earliestBookingTime,
        reason: 'elapsed'
      });
    }

    // 2. Add existing sessions with buffer
    try {
      // Use the fullDayWindow times (start and end of selected date in target timezone) - immutable
      const dateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const fullDayStart = startOfDay(dateOnly);
      
      // Use the same end time as fullDayWindow to include sessions that might end at midnight
      const nextDayDate = new Date(dateOnly.getFullYear(), dateOnly.getMonth(), dateOnly.getDate() + 1);
      const fullDayEnd = startOfDay(nextDayDate);

      // Convert fullDayWindow times to UTC for comparison
      const queryStartUtc = fromZonedTime(fullDayStart, targetTimezone);
      const queryEndUtc = fromZonedTime(fullDayEnd, targetTimezone);

      // Use service role client to bypass RLS
      const { data: allSessions, error } = await supabaseServiceRole
        .from('sessions')
        .select('id, scheduled_at, duration_min, status')
        .eq('tutor_id', tutorId)
        .in('status', ['scheduled', 'started']);

      if (error) {
        console.error('Error fetching sessions:', error);
      } else if (allSessions && allSessions.length > 0) {
        // Filter sessions that fall within the selected date
        const sessionsInRange = allSessions.filter(session => {
          try {
            const sessionStartUtc = parseISO(session.scheduled_at);
            return sessionStartUtc >= queryStartUtc && sessionStartUtc <= queryEndUtc;
          } catch (error) {
            console.error('Error parsing session date:', error, session);
            return false;
          }
        });
        
        for (const session of sessionsInRange) {
          try {
            // Parse UTC time from database
            const sessionStartUtc = parseISO(session.scheduled_at);
            
            // Convert UTC session time to target timezone
            const sessionStartInTarget = toZonedTime(sessionStartUtc, targetTimezone);
            
            // Add duration + buffer to get end time in target timezone
            const sessionEndInTarget = addMinutes(sessionStartInTarget, session.duration_min + BUFFER_TIME_MINUTES);

            // Add to unavailable periods
            unavailablePeriods.push({
              startTime: sessionStartInTarget,
              endTime: sessionEndInTarget,
              reason: 'session'
            });
          } catch (error) {
            console.error('Error processing session:', error, session);
          }
        }
      }
    } catch (error) {
      console.error('Error getting sessions:', error);
    }

    
    return unavailablePeriods;
  }

  /**
   * Subtract unavailable periods from the full day window to get available windows
   */
  private subtractUnavailablePeriods(
    fullDayWindow: AvailabilityWindow,
    unavailablePeriods: UnavailablePeriod[]
  ): AvailabilityWindow[] {
    if (unavailablePeriods.length === 0) {
      return [fullDayWindow];
    }

    // Sort unavailable periods by start time
    const sortedPeriods = unavailablePeriods.sort((a, b) => 
      a.startTime.getTime() - b.startTime.getTime()
    );

    const availableWindows: AvailabilityWindow[] = [];
    let currentStart = fullDayWindow.startTime;

    for (const period of sortedPeriods) {
      // If there's a gap before this unavailable period, add it as an available window
      if (isBefore(currentStart, period.startTime)) {
        availableWindows.push({
          startTime: new Date(currentStart),
          endTime: new Date(period.startTime),
          tutorTimezone: fullDayWindow.tutorTimezone
        });
      }

      // Move current start to after this unavailable period
      if (isAfter(period.endTime, currentStart)) {
        currentStart = new Date(period.endTime);
      }
    }

    // If there's time remaining after the last unavailable period, add it as an available window
    if (isBefore(currentStart, fullDayWindow.endTime)) {
      availableWindows.push({
        startTime: new Date(currentStart),
        endTime: new Date(fullDayWindow.endTime),
        tutorTimezone: fullDayWindow.tutorTimezone
      });
    }

    
    return availableWindows;
  }

  /**
   * NEW STEP 5: Convert tutor availability from database to target timezone and merge intervals
   */
  private convertTutorAvailabilityToTargetTimezone(
    availabilitySlots: TutorAvailabilitySlot[],
    selectedDate: Date,
    targetTimezone: string
  ): AvailabilityWindow[] {
    
    const windows: AvailabilityWindow[] = [];

    for (const slot of availabilitySlots) {
      try {
        // Convert slot from IST to target timezone for the selected date
        const { startInTarget, endInTarget } = this.convertSlotToTargetTimezone(slot, selectedDate, targetTimezone);

        
        // Check if this availability window intersects with the selected date
        const selectedDateStart = startOfDay(selectedDate);
        const selectedDateEnd = endOfDay(selectedDate);
        
        // For midnight edge case: if tutor availability ends at start of next day (24:00:00 case),
        // we should allow it to extend to exactly midnight of the selected date
        const nextDayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 1);
        const nextDayStart = startOfDay(nextDayDate); // Start of next day (midnight)

        // If the window overlaps with the selected date, add the intersection
        if ((isBefore(startInTarget, selectedDateEnd) || isEqual(startInTarget, selectedDateEnd)) && 
            (isAfter(endInTarget, selectedDateStart) || isEqual(endInTarget, selectedDateStart))) {
          const intersectionStart = isAfter(startInTarget, selectedDateStart) ? startInTarget : selectedDateStart;
          
          // Allow intersection to extend to midnight if tutor availability ends at start of next day
          let intersectionEnd: Date;
          if (isEqual(endInTarget, nextDayStart)) {
            // Special case: tutor availability ends at 24:00:00 (start of next day)
            // Allow the intersection to go to exactly midnight
            intersectionEnd = nextDayStart;
          } else {
            intersectionEnd = isBefore(endInTarget, selectedDateEnd) ? endInTarget : selectedDateEnd;
          }

          if (isBefore(intersectionStart, intersectionEnd) || isEqual(intersectionStart, intersectionEnd)) {
            windows.push({
              startTime: new Date(intersectionStart),
              endTime: new Date(intersectionEnd),
              tutorTimezone: targetTimezone
            });
          }
        }
      } catch (error) {
        console.error('Error converting availability slot:', error, slot);
      }
    }

    // Merge overlapping windows and sort
    const mergedWindows = this.mergeOverlappingWindows(windows);
    
    
    return mergedWindows;
  }

  /**
   * Convert a single availability slot to target timezone
   */
  private convertSlotToTargetTimezone(
    slot: TutorAvailabilitySlot,
    selectedDate: Date,
    targetTimezone: string
  ): { startInTarget: Date; endInTarget: Date } {
    // Calculate the date for this slot's day of week
    const selectedDayOfWeek = selectedDate.getDay(); // 0 = Sunday, 6 = Saturday
    const dayDifference = slot.day_of_week - selectedDayOfWeek;
    
    // Calculate the target date for this slot using immutable operations
    const baseYear = selectedDate.getFullYear();
    const baseMonth = selectedDate.getMonth();
    const baseDay = selectedDate.getDate();
    const slotDate = new Date(baseYear, baseMonth, baseDay + dayDifference);
    
    // Parse the time strings and create dates in IST
    const [startHours, startMinutes, startSeconds] = slot.start_time.split(":").map(Number);
    let [endHours, endMinutes, endSeconds] = slot.end_time.split(":").map(Number);
    
    // Create start time in IST (immutable)
    const startIST = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate(), 
                              startHours, startMinutes, startSeconds || 0, 0);
    
    // Create end time in IST (immutable)
    let endIST: Date;
    
    // Handle 24:00:00 case
    if (endHours === 24) {
      endHours = 0;
      // Move to next day for 24:00:00
      endIST = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate() + 1, 
                        endHours, endMinutes, endSeconds || 0, 0);
    } else {
      endIST = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate(), 
                        endHours, endMinutes, endSeconds || 0, 0);
    }

    // Fix: if end is before start (e.g., wraps to next day), adjust
    if (endIST <= startIST) {
      endIST = new Date(endIST.getFullYear(), endIST.getMonth(), endIST.getDate() + 1, 
                        endIST.getHours(), endIST.getMinutes(), endIST.getSeconds(), endIST.getMilliseconds());
    }

    // Convert from IST to UTC, then to target timezone
    const utcStart = fromZonedTime(startIST, "Asia/Kolkata");
    const utcEnd = fromZonedTime(endIST, "Asia/Kolkata");

    const startInTarget = toZonedTime(utcStart, targetTimezone);
    const endInTarget = toZonedTime(utcEnd, targetTimezone);

    return { startInTarget, endInTarget };
  }

  /**
   * NEW STEP 6: Get intersection with available windows from step 4
   */
  private getIntersectionWithAvailableWindows(
    availableWindows: AvailabilityWindow[],
    tutorAvailabilityWindows: AvailabilityWindow[]
  ): AvailabilityWindow[] {
    
    const intersectedWindows: AvailabilityWindow[] = [];

    // For each available window, find intersections with tutor availability windows
    for (const availableWindow of availableWindows) {
      for (const tutorWindow of tutorAvailabilityWindows) {
        // Find intersection between available window and tutor availability window
        const intersectionStart = isAfter(availableWindow.startTime, tutorWindow.startTime) 
          ? availableWindow.startTime 
          : tutorWindow.startTime;
        
        const intersectionEnd = isBefore(availableWindow.endTime, tutorWindow.endTime) 
          ? availableWindow.endTime 
          : tutorWindow.endTime;

        // If there's a valid intersection, add it
        if (isBefore(intersectionStart, intersectionEnd)) {
          intersectedWindows.push({
            startTime: new Date(intersectionStart),
            endTime: new Date(intersectionEnd),
            tutorTimezone: availableWindow.tutorTimezone
          });
        }
      }
    }

    // Merge overlapping windows and sort
    const finalWindows = this.mergeOverlappingWindows(intersectedWindows);
    
    
    return finalWindows;
  }

  /**
   * Generate all possible booking slots from final windows
   * Enhanced to handle DST transitions correctly
   */
  private generateBookingSlotsFromWindows(
    windows: AvailabilityWindow[],
    sessionDurationMinutes: number,
    targetTimezone: string
  ): BookingTimeSlot[] {
    const slots: BookingTimeSlot[] = [];

    for (const window of windows) {
      let currentTime = new Date(window.startTime);
      const windowEnd = window.endTime;

      while (true) {
        const slotEnd = addMinutes(currentTime, sessionDurationMinutes);
        
        // Check if the entire session duration fits within the window
        // The session can end exactly when the window ends (allow equality)
        if (isAfter(slotEnd, windowEnd)) {
          break;
        }

        // Format time string in target timezone
        const timeString = format(currentTime, 'h:mm a', { timeZone: targetTimezone });

        slots.push({
          startTime: new Date(currentTime),
          endTime: new Date(slotEnd),
          timeString,
          available: true
        });

        // Move to next slot (increment by slot interval)
        currentTime = addMinutes(currentTime, SLOT_INTERVAL_MINUTES);
      }
    }

    // Sort slots by start time and remove duplicates (DST-aware)
    const uniqueSlots = this.removeDuplicateSlotsDSTAware(slots, targetTimezone);
    return uniqueSlots.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  }

  /**
   * Merge overlapping windows
   */
  private mergeOverlappingWindows(windows: AvailabilityWindow[]): AvailabilityWindow[] {
    if (windows.length <= 1) {
      return windows;
    }

    // Sort by start time
    const sorted = windows.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    const merged: AvailabilityWindow[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const last = merged[merged.length - 1];

      // If current window overlaps with the last merged window, merge them
      if (isBefore(current.startTime, last.endTime) || isEqual(current.startTime, last.endTime)) {
        last.endTime = isAfter(current.endTime, last.endTime) ? current.endTime : last.endTime;
      } else {
        merged.push(current);
      }
    }

    return merged;
  }

  /**
   * Remove duplicate slots based on start time
   */
  private removeDuplicateSlots(slots: BookingTimeSlot[]): BookingTimeSlot[] {
    const seen = new Set<string>();
    return slots.filter(slot => {
      const key = slot.startTime.getTime().toString();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * Remove duplicate slots with DST awareness
   * During DST transitions, the same local time can occur twice (fall back)
   * or be skipped (spring forward). This method handles both cases.
   */
  private removeDuplicateSlotsDSTAware(slots: BookingTimeSlot[], targetTimezone: string): BookingTimeSlot[] {
    if (slots.length === 0) return slots;

    // Group slots by their formatted time string
    const slotsByTimeString = new Map<string, BookingTimeSlot[]>();
    
    slots.forEach(slot => {
      const timeString = slot.timeString;
      if (!slotsByTimeString.has(timeString)) {
        slotsByTimeString.set(timeString, []);
      }
      slotsByTimeString.get(timeString)!.push(slot);
    });

    const uniqueSlots: BookingTimeSlot[] = [];

    // For each time string, decide which slot(s) to keep
    slotsByTimeString.forEach((slotsForTime, timeString) => {
      if (slotsForTime.length === 1) {
        // No duplicates, keep the slot
        uniqueSlots.push(slotsForTime[0]);
      } else {
        // Multiple slots with same time string - DST transition case
        // During fall back, we want to keep the LATER occurrence (after DST ends)
        // During spring forward, this shouldn't happen as the time is skipped
        
        // Sort by actual UTC timestamp (later timestamp = after DST transition)
        const sortedSlots = slotsForTime.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        
        // For fall back transition, keep the later occurrence (PST, not PDT)
        // This is the slot that occurs after 2:00 AM becomes 1:00 AM
        const slotToKeep = sortedSlots[sortedSlots.length - 1];
        
        console.log(`DST duplicate detected for ${timeString}:`);
        console.log(`  Found ${slotsForTime.length} slots, keeping the later one (${slotToKeep.startTime.toISOString()})`);
        
        uniqueSlots.push(slotToKeep);
      }
    });

    return uniqueSlots;
  }

  /**
   * Format booking slots for display
   */
  formatBookingSlotsForDisplay(slots: BookingTimeSlot[]): string[] {
    return slots
      .filter(slot => slot.available)
      .map(slot => slot.timeString);
  }

  /**
   * Get a specific booking slot by time string
   */
  getBookingSlotByTimeString(slots: BookingTimeSlot[], timeString: string): BookingTimeSlot | null {
    return slots.find(slot => slot.timeString === timeString) || null;
  }
}

export const bookingAvailabilityService = new BookingAvailabilityService();